import React, { createContext, useContext, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import NotificationContainer from './notification-container';
import { useNotification, NotificationHook } from '../utils/useNotification';

const NotificationContext = createContext<NotificationHook | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const notificationHook = useNotification();

  return (
    <NotificationContext.Provider value={notificationHook}>
      {children}
      <NotificationContainer
        notifications={notificationHook.notifications}
        onRemove={notificationHook.removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationHook => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

// 专门用于签到的 hook
export const useCheckinNotification = () => {
  const tCheckin = useTranslations('checkin');
  const { showSuccess, showError } = useNotificationContext();

  const showCheckinSuccess = () => {
    // Localized reward notification.
    showSuccess(
      tCheckin('successTitle'),
      tCheckin('successMessage'),
      5000
    );
  };

  const showCheckinError = (reason: string) => {
    const errorMappings = [
      {
        match: 'User rejected',
        title: tCheckin('canceledTitle'),
        message: tCheckin('errors.userRejected'),
      },
      {
        match: 'insufficient funds',
        title: tCheckin('failedTitle'),
        message: tCheckin('errors.insufficientFunds'),
      },
      {
        match: 'already checked in',
        title: tCheckin('failedTitle'),
        message: tCheckin('errors.alreadyCheckedIn'),
      },
      {
        match: 'network',
        title: tCheckin('failedTitle'),
        message: tCheckin('errors.network'),
      },
    ];

    let title = tCheckin('failedTitle');
    let message = reason;

    // 根据错误原因匹配更友好的提示
    for (const mapping of errorMappings) {
      if (reason.toLowerCase().includes(mapping.match.toLowerCase())) {
        title = mapping.title;
        message = mapping.message;
        break;
      }
    }

    showError(title, message, 4000);
  };

  return {
    showCheckinSuccess,
    showCheckinError,
  };
}; 
